import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import { usePostAuthApi } from '@/hooks/UseApiCalls';
import NedarimStandingOrderDtoIn from '@/models/NedarimStandingOrderDtoIn';
import { formatDate, formatRowMoney, getPaymentSourceLabel, parseDate } from '../standingOrderModel';
import {
  OrderState,
  StandingOrderActionResult,
  actionUrl,
  canToggle,
  describeActionError,
} from '../standingOrderActions';
import { isAptOrder } from '../aptStandingOrders';
import StopReasonField from './StopReasonField';
import chargeStyles from '../AdditionalInfo/StandingOrderChargeDialog.module.css';
import styles from './OrderActions.module.css';

interface CancelOrderDialogProps {
  order: NedarimStandingOrderDtoIn;
  state: OrderState;
  fundId: number;
  onClose: () => void;
  onDone: (kind: 'cancel' | 'freeze', result: StandingOrderActionResult) => void;
}

/**
 * Cancelling deletes the order at Nedarim, and a credit order has no way back from that - so it is
 * the one action that asks twice: a box to tick before the button will work, and a freeze offered
 * in the same place, because a coordinator who might want to collect from this donor again wants
 * the freeze. The order the dialog is about is named at the top, as the charge dialog names it.
 */
const CancelOrderDialog = ({ order, state, fundId, onClose, onDone }: CancelOrderDialogProps) => {
  const { t, i18n } = useTranslation();
  const prefix = 'standingOrders.orderActions.cancelDialog';
  const [acknowledged, setAcknowledged] = useState(false);
  // Why, for APT's «מצב הוראה» - for the cancellation, or for the freeze offered instead (the office, 21.10.2026).
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [cancelled, setCancelled] = useState(false);
  /*
   * A bank order in APT (HAT-1441) is not deleted: APT marks it cancelled, the office stops sending
   * it, and the board can reactivate it (Chaya, 19.10.2026). So it has no box to tick - there is
   * nothing irreversible to acknowledge - and the warning says how to get it back. The rest is the
   * credit dialog, in its words.
   */
  const isApt = isAptOrder(order);
  const mustAcknowledge = !isApt;
  const reasonBody = () => (isApt && reason.trim() ? { reason: reason.trim() } : {});

  const refusedText = (result: StandingOrderActionResult) =>
    t('standingOrders.orderActions.errors.nedarimRefused', { message: result.message ?? '' });

  const { mutate: cancel, isPending: isCancelling } = usePostAuthApi<StandingOrderActionResult, object>(
    actionUrl(fundId, order, 'cancel'),
    {
      onSuccess: (result) => {
        if (!result.success) {
          setError(refusedText(result));
          return;
        }
        setCancelled(true);
        onDone('cancel', result);
      },
      onError: (e) => setError(describeActionError(e, t, isApt)),
    },
  );

  const { mutate: freeze, isPending: isFreezing } = usePostAuthApi<StandingOrderActionResult, object>(
    actionUrl(fundId, order, 'freeze'),
    {
      onSuccess: (result) => {
        if (!result.success) {
          setError(refusedText(result));
          return;
        }
        onDone('freeze', result);
        onClose();
      },
      onError: (e) => setError(describeActionError(e, t, isApt)),
    },
  );

  const busy = isCancelling || isFreezing;
  const name = order.clientName || t('standingOrders.donor');
  const source = getPaymentSourceLabel(order);
  const next = parseDate(order.nextDate);
  const amount = formatRowMoney(order.amount, order, i18n.language);
  // Only an order that is live can be frozen instead; a frozen or ended one has nothing to stop.
  const offerFreeze = state === 'enabled' && canToggle(state);

  return (
    <MainDialogLayout
      open
      onOpenChange={(open: boolean) => { if (!open && !busy) onClose(); }}
      title={t(`${prefix}.title`, { name })}
      maxWidth="640px"
      fitContent
      contentClassName={chargeStyles.compactDialog}
    >
      <div className={chargeStyles.container}>
        <div className={chargeStyles.form}>
          <div className={chargeStyles.context}>
            <span>
              {[name, source && t(isApt ? 'standingOrders.orderActions.bank.cancelDialog.account' : `${prefix}.card`, { source })]
                .filter(Boolean).join(' · ')}
            </span>
            <b>
              {next
                ? t(`${prefix}.context`, { amount, date: formatDate(next, i18n.language) })
                : t(`${prefix}.contextNoDate`, { amount })}
            </b>
          </div>

          {cancelled ? (
            <>
              <div role="status" className={chargeStyles.successMessage}>{t(`${prefix}.done`)}</div>
              <div className={chargeStyles.actions}>
                <Button variant="primary" onClick={onClose}>{t(`${prefix}.close`)}</Button>
              </div>
            </>
          ) : (
            <>
              <div className={styles.dangerBox}>
                <p className={styles.dangerText}>
                  {t(isApt ? 'standingOrders.orderActions.bank.cancelDialog.warning' : `${prefix}.warning`)}
                </p>
                {mustAcknowledge && (
                  <label className={styles.dangerCheckbox} htmlFor="cancel-standing-order-acknowledge">
                    <input
                      id="cancel-standing-order-acknowledge"
                      type="checkbox"
                      checked={acknowledged}
                      onChange={(e) => setAcknowledged(e.target.checked)}
                      disabled={busy}
                    />
                    <span>{t(`${prefix}.acknowledge`)}</span>
                  </label>
                )}
              </div>

              {isApt && (
                <StopReasonField id="cancel-standing-order-reason" value={reason} onChange={setReason} disabled={busy} />
              )}

              {error && <div role="alert" className={chargeStyles.errorMessage}>{error}</div>}

              <div className={chargeStyles.actions}>
                <Button
                  variant="primary"
                  disabled={(mustAcknowledge && !acknowledged) || busy}
                  onClick={() => { setError(null); cancel(reasonBody()); }}
                >
                  {isCancelling ? t(`${prefix}.cancelling`) : t(`${prefix}.confirm`)}
                </Button>
                <span className={styles.secondaryActions}>
                  {offerFreeze && (
                    <Button
                      variant="outline"
                      className="!h-[52px] !min-w-[140px] !px-6"
                      disabled={busy}
                      onClick={() => { setError(null); freeze(reasonBody()); }}
                    >
                      {isFreezing ? t(`${prefix}.freezing`) : t(`${prefix}.freezeInstead`)}
                    </Button>
                  )}
                  <button type="button" className={styles.linkButton} disabled={busy} onClick={onClose}>
                    {t(`${prefix}.back`)}
                  </button>
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default CancelOrderDialog;
